import { useState, useEffect, useCallback, useRef } from "react";
import { authClient } from "../auth/client";
import { showToast } from "./Toast";
import { slugify, nextSlugCandidate } from "./orgSlug";
import { friendlyAuthError, isOrgAlreadyExists } from "./authErrors";
import { normalizeEmail } from "../auth/AuthGate";
import "./OrgManager.css";

// 9 retries = 10 total attempts (base + 9 suffixed). Chosen to be aggressive
// enough that two users picking the same name silently win, while bounding the
// worst-case round-trip cost.
export const MAX_SLUG_RETRIES = 9;

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo?: string | null;
  metadata?: any;
}

interface Member {
  id: string;
  userId: string;
  organizationId: string;
  role: string;
  createdAt: Date;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export default function OrgManager() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [activeOrg, setActiveOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newOrgName, setNewOrgName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const activeOrgRef = useRef(activeOrg);
  activeOrgRef.current = activeOrg;

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await authClient.organization.list();
      if (err) throw err;

      const list = Array.isArray(data) ? data : (data as any).data;
      if (!Array.isArray(list)) {
        setOrgs([]);
        return;
      }

      setOrgs(list);
      if (list.length > 0 && !activeOrgRef.current) {
        setActiveOrg(list[0]);
      }
    } catch (err: any) {
      setError(err.message || (err.error && err.error.message) || "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async (orgId: string) => {
    try {
      const { data, error: err } = await (authClient.organization as any).listMembers({
        query: { organizationId: orgId }
      });
      if (err) throw err;

      const list = Array.isArray(data) ? data : (data as any).data;
      setMembers(Array.isArray(list) ? list : []);
    } catch (err: any) {
      setError(err.message || (err.error && err.error.message) || "Failed to load members");
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  useEffect(() => {
    if (activeOrg) {
      fetchMembers(activeOrg.id);
    }
  }, [activeOrg, fetchMembers]);

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;
    setError("");

    const baseSlug = slugify(newOrgName);
    if (!baseSlug) {
      setError("Team name must contain at least one letter or number.");
      return;
    }

    setSubmitting(true);
    try {
      let lastErr: unknown = null;
      let created: Organization | null = null;

      for (let attempt = 0; attempt <= MAX_SLUG_RETRIES; attempt++) {
        const candidate = attempt === 0 ? baseSlug : nextSlugCandidate(baseSlug, attempt);
        const { data, error: err } = await authClient.organization.create({
          name: newOrgName,
          slug: candidate,
        });
        if (!err) {
          created = unwrapOrg(data);
          break;
        }
        lastErr = err;
        if (!isOrgAlreadyExists(err)) break;
      }

      if (!created) throw lastErr ?? new Error("Could not create team");

      showToast("Team created", "success");
      setNewOrgName("");
      setShowCreate(false);
      setActiveOrg(created);
      await fetchOrgs();
    } catch (err: unknown) {
      const msg = friendlyAuthError(err, "Failed to create team");
      setError(msg);
      showToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  function unwrapOrg(data: unknown): Organization | null {
    if (!data || typeof data !== "object") return null;
    const candidate = "data" in data ? (data as { data: unknown }).data : data;
    if (!candidate || typeof candidate !== "object" || !("id" in candidate)) return null;
    return candidate as Organization;
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrg || !inviteEmail.trim()) return;
    setError("");
    try {
      const { error: err } = await authClient.organization.inviteMember({
        organizationId: activeOrg.id,
        email: normalizeEmail(inviteEmail),
        role: inviteRole as any,
      });
      if (err) throw err;
      setInviteEmail("");
      showToast("Invitation sent", "success");
    } catch (err: any) {
      const msg = err.message || (err.error && err.error.message) || "Failed to send invitation";
      setError(msg);
      showToast("Failed to send invitation", "error");
    }
  };

  if (loading && orgs.length === 0) {
    return <div className="org-manager-loading">Loading teams...</div>;
  }

  return (
    <div className="org-manager-container">
      <div className="org-manager-sidebar">
        <div className="org-manager-sidebar-header">
          <span className="org-manager-sidebar-title">Teams</span>
          <button onClick={() => setShowCreate(!showCreate)} className="org-manager-add-btn" title="Create Team">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3v10M3 8h10"/>
            </svg>
          </button>
        </div>

        {showCreate && (
          <form onSubmit={handleCreateOrg} className="org-manager-create-form">
            <input
              className="org-manager-input"
              placeholder="Team name"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              autoFocus
              disabled={submitting}
            />
            <div className="org-manager-form-actions">
              <button type="button" onClick={() => setShowCreate(false)} className="org-manager-cancel-btn" disabled={submitting}>Cancel</button>
              <button type="submit" className="org-manager-save-btn" disabled={submitting}>
                {submitting ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        )}

        <div className="org-manager-org-list">
          {orgs.map((org) => (
            <button
              key={org.id}
              onClick={() => setActiveOrg(org)}
              className={`org-manager-org-item ${activeOrg?.id === org.id ? "active" : ""}`}
            >
              <div className="org-manager-org-avatar">
                {org.name.charAt(0).toUpperCase()}
              </div>
              <span className="org-manager-org-name">{org.name}</span>
            </button>
          ))}
          {orgs.length === 0 && !showCreate && (
            <div className="org-manager-empty-sidebar">No teams yet</div>
          )}
        </div>
      </div>

      <div className="org-manager-content">
        {activeOrg ? (
          <>
            <div className="org-manager-content-header">
              <h3 className="org-manager-content-title">{activeOrg.name}</h3>
              <div className="org-manager-org-slug">slug: {activeOrg.slug}</div>
            </div>

            {error && <div className="org-manager-error">{error}</div>}

            <div className="org-manager-section">
              <h4 className="org-manager-section-title">Invite Member</h4>
              <form onSubmit={handleInvite} className="org-manager-invite-form">
                <input
                  className="org-manager-input"
                  style={{ flex: 1 }}
                  type="email"
                  placeholder="Email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                />
                <select
                  className="org-manager-select"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
                <button type="submit" className="org-manager-save-btn">Invite</button>
              </form>
            </div>

            <div className="org-manager-section">
              <h4 className="org-manager-section-title">Members ({members.length})</h4>
              <div className="org-manager-member-list">
                {members.map((m) => (
                  <div key={m.id} className="org-manager-member-item">
                    <div className="org-manager-member-info">
                      <div className="org-manager-member-name">{m.user?.name || "Unknown"}</div>
                      <div className="org-manager-member-email">{m.user?.email}</div>
                    </div>
                    <span className={`org-manager-role-badge ${m.role}`}>
                      {m.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="org-manager-empty-content">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--border-default)" strokeWidth="1">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            {error && <div className="org-manager-error" style={{ marginTop: 20 }}>{error}</div>}
            <p>Select a team or create a new one to manage members and shared documents.</p>
          </div>
        )}
      </div>
    </div>
  );
}
